import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import Layout from "../../Component/Layout/Layout";
import { useTheme } from "../../Context/ThemeContext";
import { motion } from "framer-motion";

const Ads = () => {
  const { darkMode } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [isWatching, setIsWatching] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [canClaim, setCanClaim] = useState(false);
  const [earnedAmount, setEarnedAmount] = useState(0);
  const [nextAdTime, setNextAdTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);

  const handleWatchNow = () => {
    setIsWatching(true);
    setCountdown(10);
    setCanClaim(false);
  };

  const handleClaim = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        "https://bioopay-full-backend.onrender.com/api/v1/ads/watch-ad"
      );

      if (response.data.success) {
        setEarnedAmount(response.data.earned);
        // Set next available time (24 hours from now)
        const nextTime = new Date();
        nextTime.setHours(nextTime.getHours() + 24);
        setNextAdTime(nextTime);

        toast.success(
          `$${response.data.earned.toFixed(2)} claimed successfully!`,
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: darkMode ? "dark" : "light",
          }
        );
      } else {
        toast.error(response.data.message || "Failed to claim earnings", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: darkMode ? "dark" : "light",
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: darkMode ? "dark" : "light",
      });
    } finally {
      setIsLoading(false);
      setIsWatching(false);
      setCanClaim(false);
    }
  };

  // Countdown timer for ad watching
  useEffect(() => {
    let timer;
    if (isWatching && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (isWatching && countdown === 0) {
      setCanClaim(true);
    }
    return () => clearTimeout(timer);
  }, [isWatching, countdown]);

  // Countdown timer for next available ad
  useEffect(() => {
    const timer = setInterval(() => {
      if (nextAdTime) {
        const now = new Date();
        const diff = nextAdTime - now;

        if (diff <= 0) {
          setNextAdTime(null);
          setTimeLeft(0);
          clearInterval(timer);
        } else {
          const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
          const minutes = Math.floor((diff / (1000 * 60)) % 60);
          const seconds = Math.floor((diff / 1000) % 60);
          setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [nextAdTime]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Background gradient based on theme
  const backgroundGradient = darkMode
    ? "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)"
    : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 50%, #1e40af 100%)";

  // Card background based on theme
  const cardBackground = darkMode
    ? "rgba(20, 20, 50, 0.7)"
    : "rgba(255, 255, 255, 0.2)";

  // Text colors based on theme
  const textColor = darkMode ? "#fff" : "#1a202c";
  const secondaryTextColor = darkMode
    ? "rgba(255,255,255,0.7)"
    : "rgba(0,0,0,0.7)";

  return (
    <Layout>
      <div
        style={{
          background: backgroundGradient,
          minHeight: "100vh",
          padding: "2rem",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: textColor,
          fontFamily: "'Poppins', sans-serif",
          transition: "background 0.3s ease",
        }}
      >
        <motion.div
          style={{
            background: cardBackground,
            borderRadius: "20px",
            padding: "2.5rem",
            width: "100%",
            maxWidth: "500px",
            boxShadow: darkMode
              ? "0 15px 30px rgba(0, 0, 0, 0.4)"
              : "0 15px 30px rgba(0, 0, 0, 0.1)",
            backdropFilter: "blur(12px)",
            border: darkMode
              ? "1px solid rgba(255, 255, 255, 0.15)"
              : "1px solid rgba(255, 255, 255, 0.3)",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
            transition: "all 0.3s ease",
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Decorative elements */}
          <div
            style={{
              position: "absolute",
              top: "-50px",
              right: "-50px",
              width: "150px",
              height: "150px",
              borderRadius: "50%",
              background: darkMode
                ? "radial-gradient(circle, rgba(76,201,240,0.2) 0%, rgba(76,201,240,0) 70%)"
                : "radial-gradient(circle, rgba(59,130,246,0.2) 0%, rgba(59,130,246,0) 70%)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "-30px",
              left: "-30px",
              width: "100px",
              height: "100px",
              borderRadius: "50%",
              background: darkMode
                ? "radial-gradient(circle, rgba(243,104,224,0.2) 0%, rgba(243,104,224,0) 70%)"
                : "radial-gradient(circle, rgba(239,68,68,0.2) 0%, rgba(239,68,68,0) 70%)",
            }}
          />

          <h1
            style={{
              fontSize: "2rem",
              marginBottom: "1rem",
              fontWeight: "700",
              background: darkMode
                ? "linear-gradient(90deg, #4cc9f0, #f72585)"
                : "linear-gradient(90deg, #3b82f6, #ef4444)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "0 2px 10px rgba(0,0,0,0.2)",
            }}
          >
            Watch & Earn
          </h1>

          <p
            style={{
              fontSize: "1rem",
              marginBottom: "2rem",
              color: secondaryTextColor,
              lineHeight: "1.6",
            }}
          >
            Watch a short advertisement for 10 seconds and earn money instantly
            to your account!
          </p>

          {nextAdTime ? (
            <div
              style={{
                background: darkMode
                  ? "rgba(0,0,0,0.3)"
                  : "rgba(255,255,255,0.2)",
                padding: "1.5rem",
                borderRadius: "12px",
                marginBottom: "1.5rem",
                border: darkMode
                  ? "1px solid rgba(255,255,255,0.1)"
                  : "1px solid rgba(0,0,0,0.1)",
              }}
            >
              <h3
                style={{
                  margin: "0 0 1rem",
                  color: darkMode ? "#4cc9f0" : "#3b82f6",
                }}
              >
                Next Ad Available In:
              </h3>
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  color: textColor,
                  fontFamily: "'Courier New', monospace",
                }}
              >
                {timeLeft}
              </div>
            </div>
          ) : !isWatching ? (
            <motion.button
              style={{
                background: darkMode
                  ? "linear-gradient(45deg, #7209b7, #3a0ca3)"
                  : "linear-gradient(45deg, #3b82f6, #1d4ed8)",
                color: "white",
                border: "none",
                padding: "1.2rem 2rem",
                fontSize: "1.1rem",
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.3s ease",
                width: "100%",
                margin: "1rem 0",
                fontWeight: "600",
                boxShadow: darkMode
                  ? "0 8px 20px rgba(114,9,183,0.4)"
                  : "0 8px 20px rgba(59,130,246,0.4)",
                position: "relative",
                overflow: "hidden",
                zIndex: "1",
                ...(isLoading && {
                  background: "#6c757d",
                  cursor: "not-allowed",
                }),
              }}
              onClick={handleWatchNow}
              disabled={isLoading}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              {isLoading ? (
                "Loading..."
              ) : (
                <>
                  <span style={{ position: "relative", zIndex: "2" }}>
                    WATCH NOW
                  </span>
                  <span
                    style={{
                      position: "absolute",
                      top: "0",
                      left: "0",
                      width: "100%",
                      height: "100%",
                      background:
                        "linear-gradient(45deg, rgba(255,255,255,0.1), rgba(255,255,255,0))",
                      zIndex: "1",
                    }}
                  />
                </>
              )}
            </motion.button>
          ) : (
            <div style={{ marginTop: "1.5rem" }}>
              <div
                style={{
                  background: darkMode
                    ? "rgba(0, 0, 0, 0.3)"
                    : "rgba(255, 255, 255, 0.2)",
                  borderRadius: "12px",
                  padding: "1.5rem",
                  marginBottom: "1.5rem",
                  border: darkMode
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(0,0,0,0.1)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "0",
                    left: "0",
                    width: `${100 - countdown * 10}%`,
                    height: "4px",
                    background: darkMode
                      ? "linear-gradient(90deg, #f72585, #7209b7)"
                      : "linear-gradient(90deg, #ef4444, #3b82f6)",
                    transition: "width 1s linear",
                  }}
                />

                <h3 style={{ margin: "0 0 0.5rem", color: textColor }}>
                  Advertisement
                </h3>
                <p
                  style={{
                    margin: "0 0 1rem",
                    color: secondaryTextColor,
                    fontSize: "0.9rem",
                  }}
                >
                  Please watch the full ad to claim your reward
                </p>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    marginTop: "1rem",
                  }}
                >
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      border: darkMode
                        ? "4px solid #3a0ca3"
                        : "4px solid #1d4ed8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "1rem",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        border: "4px solid transparent",
                        borderTopColor: darkMode ? "#4cc9f0" : "#3b82f6",
                        animation: "spin 1s linear infinite",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "bold",
                        color: textColor,
                      }}
                    >
                      {countdown}
                    </span>
                  </div>
                  <span
                    style={{
                      color:
                        countdown > 0
                          ? darkMode
                            ? "#f72585"
                            : "#ef4444"
                          : darkMode
                          ? "#4cc9f0"
                          : "#3b82f6",
                      fontWeight: "bold",
                      fontSize: countdown > 0 ? "1rem" : "1.2rem",
                    }}
                  >
                    {countdown > 0
                      ? `${formatTime(countdown)} remaining`
                      : "Ready to Claim!"}
                  </span>
                </div>
              </div>

              {canClaim && (
                <motion.button
                  style={{
                    background: darkMode
                      ? "linear-gradient(45deg, #4cc9f0, #4895ef)"
                      : "linear-gradient(45deg, #3b82f6, #2563eb)",
                    color: "white",
                    border: "none",
                    padding: "1.2rem 2rem",
                    fontSize: "1.1rem",
                    borderRadius: "12px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    width: "100%",
                    margin: "1rem 0",
                    fontWeight: "600",
                    boxShadow: darkMode
                      ? "0 8px 20px rgba(76,201,240,0.4)"
                      : "0 8px 20px rgba(59,130,246,0.4)",
                    ...(isLoading && {
                      background: "#6c757d",
                      cursor: "not-allowed",
                    }),
                  }}
                  onClick={handleClaim}
                  disabled={isLoading}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isLoading ? "Processing..." : "CLAIM NOW"}
                </motion.button>
              )}
            </div>
          )}

          {earnedAmount > 0 && (
            <motion.div
              style={{
                marginTop: "1.5rem",
                padding: "1.2rem",
                background: darkMode
                  ? "linear-gradient(90deg, rgba(76,201,240,0.1), rgba(114,9,183,0.1))"
                  : "linear-gradient(90deg, rgba(59,130,246,0.1), rgba(239,68,68,0.1))",
                borderRadius: "12px",
                borderLeft: darkMode
                  ? "4px solid #4cc9f0"
                  : "4px solid #3b82f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span
                style={{
                  color: darkMode ? "#4cc9f0" : "#3b82f6",
                  fontSize: "1.5rem",
                }}
              >
                💰
              </span>
              <p style={{ fontSize: "1.2rem", margin: "0", color: textColor }}>
                You earned:{" "}
                <span
                  style={{
                    color: darkMode ? "#4cc9f0" : "#3b82f6",
                    fontWeight: "bold",
                  }}
                >
                  ${earnedAmount.toFixed(2)}
                </span>
              </p>
            </motion.div>
          )}

          <div
            style={{
              marginTop: "2rem",
              textAlign: "left",
              background: darkMode
                ? "rgba(0,0,0,0.2)"
                : "rgba(255,255,255,0.2)",
              padding: "1rem",
              borderRadius: "10px",
              border: darkMode
                ? "1px solid rgba(255,255,255,0.05)"
                : "1px solid rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.8rem",
                marginBottom: "0.8rem",
                color: secondaryTextColor,
                fontSize: "0.9rem",
              }}
            >
              <span
                style={{
                  color: darkMode ? "#4cc9f0" : "#3b82f6",
                  background: darkMode
                    ? "rgba(76,201,240,0.1)"
                    : "rgba(59,130,246,0.1)",
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ⓘ
              </span>
              <span>You can watch one ad every 24 hours</span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.8rem",
                color: secondaryTextColor,
                fontSize: "0.9rem",
              }}
            >
              <span
                style={{
                  color: darkMode ? "#f72585" : "#ef4444",
                  background: darkMode
                    ? "rgba(247,37,133,0.1)"
                    : "rgba(239,68,68,0.1)",
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                💰
              </span>
              <span>Earnings depend on your current investment plan</span>
            </div>
          </div>
        </motion.div>

        <style>{`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.02); }
            100% { transform: scale(1); }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          button:hover:not(:disabled) {
            transform: translateY(-3px);
            box-shadow: ${
              darkMode
                ? "0 10px 25px rgba(114,9,183,0.6) !important"
                : "0 10px 25px rgba(59,130,246,0.6) !important"
            };
          }
        `}</style>
      </div>
    </Layout>
  );
};

export default Ads;
